import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { WhatsAppQuickReplies } from "../components/WhatsAppQuickReplies";
import {
  BotMessageSquare,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Clock,
  MessageCircle,
  Zap,
  Search,
  Send,
  Bell,
  Moon,
  Sun,
  AlertCircle,
  CheckCircle
} from "lucide-react";

const TRIGGER_TYPES = [
  { id: "welcome", label: "Bienvenida", description: "Se envía cuando un cliente escribe por primera vez", icon: MessageCircle },
  { id: "out_of_hours", label: "Fuera de Horario", description: "Se envía fuera del horario de atención", icon: Moon },
  { id: "keyword", label: "Palabra Clave", description: "Se activa cuando el cliente menciona ciertas palabras", icon: Search },
  { id: "no_response", label: "Sin Respuesta", description: "Se envía si no hay respuesta en X minutos", icon: Clock }
];

export default function WhatsAppRespuestasPage() {
  const [responses, setResponses] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("automaticas");
  const [editingResponse, setEditingResponse] = useState(null);
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders, user } = useAuth();

  const [formData, setFormData] = useState({
    config_id: "",
    name: "",
    trigger_type: "welcome",
    trigger_keywords: [],
    no_response_minutes: 5,
    message: "",
    is_active: true,
    departamento_ids: []
  });

  const [keywordInput, setKeywordInput] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [responsesRes, configsRes, deptosRes] = await Promise.all([
        axios.get(`${API}/whatsapp/auto-responses`, getAuthHeaders()),
        axios.get(`${API}/whatsapp/config`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders())
      ]);
      setResponses(responsesRes.data || []);
      setConfigs(configsRes.data || []);
      setDepartamentos(deptosRes.data || []);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      config_id: configs[0]?.id || "",
      name: "",
      trigger_type: "welcome",
      trigger_keywords: [],
      no_response_minutes: 5,
      message: "",
      is_active: true,
      departamento_ids: []
    });
    setKeywordInput("");
    setEditingResponse(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (response) => {
    setEditingResponse(response);
    setFormData({
      config_id: response.config_id || "",
      name: response.name || "",
      trigger_type: response.trigger_type || "welcome",
      trigger_keywords: response.trigger_keywords || [],
      no_response_minutes: response.no_response_minutes || 5,
      message: response.message || "",
      is_active: response.is_active !== false,
      departamento_ids: response.departamento_ids || []
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.message || !formData.config_id) {
      toast.error("Nombre, mensaje y configuración son requeridos");
      return;
    }

    if (formData.trigger_type === "keyword" && formData.trigger_keywords.length === 0) {
      toast.error("Agrega al menos una palabra clave");
      return;
    }

    if (formData.trigger_type === "no_response" && (!formData.no_response_minutes || formData.no_response_minutes < 1)) {
      toast.error("Indica el tiempo sin respuesta (mínimo 1 minuto)");
      return;
    }

    try {
      setSaving(true);
      if (editingResponse) {
        await axios.put(`${API}/whatsapp/auto-responses/${editingResponse.id}`, formData, getAuthHeaders());
        toast.success("Respuesta actualizada");
      } else {
        await axios.post(`${API}/whatsapp/auto-responses`, formData, getAuthHeaders());
        toast.success("Respuesta creada");
      }
      setShowDialog(false);
      fetchData();
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (responseId) => {
    if (!confirm("¿Está seguro de eliminar esta respuesta automática?")) return;
    try {
      await axios.delete(`${API}/whatsapp/auto-responses/${responseId}`, getAuthHeaders());
      toast.success("Respuesta eliminada");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const toggleActive = async (response) => {
    try {
      await axios.put(`${API}/whatsapp/auto-responses/${response.id}`, {
        ...response,
        is_active: !response.is_active
      }, getAuthHeaders());
      toast.success(response.is_active ? "Respuesta desactivada" : "Respuesta activada");
      fetchData();
    } catch (err) {
      toast.error("Error al cambiar estado");
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.trigger_keywords.includes(keywordInput.trim().toLowerCase())) {
      setFormData({
        ...formData,
        trigger_keywords: [...formData.trigger_keywords, keywordInput.trim().toLowerCase()]
      });
      setKeywordInput("");
    }
  };

  const removeKeyword = (keyword) => {
    setFormData({
      ...formData,
      trigger_keywords: formData.trigger_keywords.filter(k => k !== keyword)
    });
  };

  const getConfigName = (configId) => {
    const config = configs.find(c => c.id === configId);
    return config?.name || "Sin configuración";
  };

  const getDeptoName = (depId) => {
    const d = departamentos.find(dep => dep.id === depId);
    return d?.name || d?.nombre || "Departamento";
  };

  const toggleDepto = (depId) => {
    setFormData((prev) => ({
      ...prev,
      departamento_ids: prev.departamento_ids.includes(depId)
        ? prev.departamento_ids.filter(id => id !== depId)
        : [...prev.departamento_ids, depId]
    }));
  };

  const getTriggerInfo = (type) => {
    return TRIGGER_TYPES.find(t => t.id === type) || TRIGGER_TYPES[0];
  };

  const isAdmin = user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrador";

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="whatsapp-respuestas-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
              <BotMessageSquare className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Respuestas Automáticas
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Configura mensajes automáticos para WhatsApp
              </p>
            </div>
          </div>
          {isAdmin && activeTab === "automaticas" && (
            <Button 
              onClick={openNewDialog}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="new-response-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Respuesta
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="automaticas" data-testid="tab-automaticas">Automáticas</TabsTrigger>
            <TabsTrigger value="rapidas" data-testid="tab-rapidas">Respuestas Rápidas</TabsTrigger>
          </TabsList>

          <TabsContent value="automaticas">
        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {TRIGGER_TYPES.map((trigger) => {
            const count = responses.filter(r => r.trigger_type === trigger.id).length;
            const activeCount = responses.filter(r => r.trigger_type === trigger.id && r.is_active).length;
            const TriggerIcon = trigger.icon;
            return (
              <Card key={trigger.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                      <TriggerIcon className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">{trigger.label}</p>
                      <p className="text-sm text-slate-500">
                        {activeCount}/{count} activas
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Responses List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : configs.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-amber-400 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">Sin configuración de WhatsApp</h3>
              <p className="text-slate-500 mb-4">Primero configura un número de WhatsApp</p>
              <Button onClick={() => window.location.href = "/whatsapp/configuracion"} variant="outline">
                Ir a Configuración
              </Button>
            </CardContent>
          </Card>
        ) : responses.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="py-12 text-center">
              <BotMessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">No hay respuestas automáticas</h3>
              <p className="text-slate-500 mb-4">Crea tu primera respuesta automática</p>
              {isAdmin && (
                <Button onClick={openNewDialog} className="bg-amber-500 hover:bg-amber-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Respuesta
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {responses.map((response) => {
              const triggerInfo = getTriggerInfo(response.trigger_type);
              const TriggerIcon = triggerInfo.icon;
              return (
                <Card 
                  key={response.id} 
                  className={`hover:shadow-lg transition-shadow ${!response.is_active ? 'opacity-60' : ''}`}
                  data-testid={`response-card-${response.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          response.is_active ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-slate-100'
                        }`}>
                          <TriggerIcon className={`w-5 h-5 ${response.is_active ? 'text-amber-600' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{response.name}</CardTitle>
                          <p className="text-sm text-slate-500">{triggerInfo.label}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={response.is_active}
                          onCheckedChange={() => toggleActive(response)}
                        />
                        <Badge variant={response.is_active ? "default" : "secondary"}>
                          {response.is_active ? "Activa" : "Inactiva"}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* Config */}
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <MessageCircle className="w-4 h-4" />
                        <span>{getConfigName(response.config_id)}</span>
                      </div>

                      {/* Departamentos */}
                      <div className="flex flex-wrap gap-1" data-testid={`response-deptos-${response.id}`}>
                        {(response.departamento_ids || []).length === 0 ? (
                          <Badge variant="secondary" className="text-xs">General (todos los departamentos)</Badge>
                        ) : (
                          response.departamento_ids.map((depId) => (
                            <Badge key={depId} variant="outline" className="text-xs border-amber-300 text-amber-700">
                              {getDeptoName(depId)}
                            </Badge>
                          ))
                        )}
                      </div>

                      {/* Keywords */}
                      {response.trigger_type === "keyword" && response.trigger_keywords?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {response.trigger_keywords.map((kw, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {kw}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* No Response Time */}
                      {response.trigger_type === "no_response" && (
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-amber-600" />
                          <span className="text-slate-600">
                            Se activa después de <strong>{response.no_response_minutes || 5}</strong> minutos sin respuesta
                          </span>
                        </div>
                      )}

                      {/* Message Preview */}
                      <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-3">
                          {response.message}
                        </p>
                      </div>

                      {/* Actions */}
                      {isAdmin && (
                        <div className="flex gap-2 pt-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => openEditDialog(response)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(response.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

          </TabsContent>

          <TabsContent value="rapidas">
            <WhatsAppQuickReplies isAdmin={isAdmin} getAuthHeaders={getAuthHeaders} />
          </TabsContent>
        </Tabs>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingResponse ? "Editar Respuesta Automática" : "Nueva Respuesta Automática"}
              </DialogTitle>
              <DialogDescription>
                Configura cuándo y qué mensaje enviar automáticamente
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Configuración de WhatsApp *</Label>
                <Select
                  value={formData.config_id}
                  onValueChange={(v) => setFormData({...formData, config_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar configuración..." />
                  </SelectTrigger>
                  <SelectContent>
                    {configs.map((config) => (
                      <SelectItem key={config.id} value={config.id}>
                        {config.name} ({config.phone_number})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Nombre de la respuesta *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ej: Bienvenida inicial"
                />
              </div>

              <div>
                <Label>Tipo de activación *</Label>
                <Select
                  value={formData.trigger_type}
                  onValueChange={(v) => setFormData({...formData, trigger_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIGGER_TYPES.map((trigger) => (
                      <SelectItem key={trigger.id} value={trigger.id}>
                        <div className="flex flex-col">
                          <span>{trigger.label}</span>
                          <span className="text-xs text-slate-500">{trigger.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.trigger_type === "keyword" && (
                <div>
                  <Label>Palabras clave</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      placeholder="Agregar palabra clave..."
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                    />
                    <Button type="button" onClick={addKeyword} variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {formData.trigger_keywords.map((kw, idx) => (
                      <Badge 
                        key={idx} 
                        variant="secondary"
                        className="cursor-pointer hover:bg-red-100"
                        onClick={() => removeKeyword(kw)}
                      >
                        {kw} ×
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {formData.trigger_type === "no_response" && (
                <div>
                  <Label>Tiempo sin respuesta (minutos) *</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="1"
                      max="60"
                      value={formData.no_response_minutes}
                      onChange={(e) => setFormData({...formData, no_response_minutes: parseInt(e.target.value) || 5})}
                      className="w-24"
                    />
                    <span className="text-sm text-slate-500">minutos</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    El mensaje se enviará si el cliente no recibe respuesta en este tiempo
                  </p>
                </div>
              )}

              <div>
                <Label>Departamentos</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Si seleccionas departamentos, este mensaje solo se enviará en conversaciones asignadas a usuarios de esos departamentos. Sin selección, aplica como mensaje general.
                </p>
                <div className="max-h-36 overflow-y-auto border rounded-lg p-2 space-y-1" data-testid="departamentos-selector">
                  {departamentos.map((dep) => (
                    <label key={dep.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 rounded px-2 py-1">
                      <input
                        type="checkbox"
                        checked={formData.departamento_ids.includes(dep.id)}
                        onChange={() => toggleDepto(dep.id)}
                        className="accent-amber-500"
                        data-testid={`depto-check-${dep.id}`}
                      />
                      <span>{dep.name || dep.nombre}</span>
                    </label>
                  ))}
                  {departamentos.length === 0 && (
                    <p className="text-xs text-slate-400 px-2 py-1">No hay departamentos registrados</p>
                  )}
                </div>
              </div>

              <div>
                <Label>Mensaje *</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Escribe el mensaje automático..."
                  rows={4}
                />
                <p className="text-xs text-slate-500 mt-1">
                  {formData.message.length}/1000 caracteres
                </p>
              </div>

              {/* Vista previa como burbuja de WhatsApp */}
              <div>
                <Label>Vista Previa</Label>
                <div
                  className="mt-1 rounded-lg p-4 bg-[#e5ddd5] dark:bg-[#0b141a]"
                  style={{ backgroundImage: "radial-gradient(circle, rgba(0,0,0,0.03) 1px, transparent 1px)", backgroundSize: "14px 14px" }}
                  data-testid="whatsapp-preview"
                >
                  <div className="max-w-[85%] bg-white dark:bg-[#202c33] rounded-lg rounded-tl-none px-3 py-2 shadow-sm relative">
                    <p className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap break-words" data-testid="whatsapp-preview-text">
                      {formData.message || "Aquí verás el mensaje tal como lo recibirá el cliente..."}
                    </p>
                    <div className="flex items-center justify-end gap-1 mt-1">
                      <span className="text-[10px] text-slate-400">
                        {new Date().toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <svg viewBox="0 0 16 11" width="14" height="10" className="text-[#53bdeb]" fill="currentColor">
                        <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.146.47.47 0 0 0-.343.146l-.311.31a.445.445 0 0 0-.14.337c0 .136.047.25.14.343l3.053 3.037c.093.086.2.13.32.13.136 0 .25-.044.343-.13l6.86-8.437a.416.416 0 0 0 .094-.318.437.437 0 0 0-.163-.311l-.237-.401z"/>
                        <path d="M15.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-.767-.724-.845 1.042 1.335 1.33c.093.085.2.129.32.129.136 0 .25-.044.343-.13l6.86-8.437a.416.416 0 0 0 .094-.318.437.437 0 0 0-.163-.311l-.302-.293z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                />
                <Label>Respuesta activa</Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingResponse ? "Guardar Cambios" : "Crear Respuesta"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
